import { lazy, Suspense, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import type { MotionValue } from 'framer-motion';
import type { ProjectSpecs } from '../lib/api';

// 3D materials renderer is code-split so the one-liner path (which is
// plain SVG) doesn't pull in three.js / r3f / postprocessing.
const HouseScene3D = lazy(() =>
  import('./HouseScene3D').then((m) => ({ default: m.HouseScene3D })),
);

/**
 * Gênese · cinematic unveil (Wave 44 + 44.1 + 44.2).
 *
 * Single-story linear volume on two lots (09 + 10). Long floating roof,
 * stacked façade of cobogó de concreto + ripado de madeira + painéis
 * metálicos + pedras moído, glass/door at the entrance.
 *
 * Two rendering variants share the same motion rig:
 *   - 'materials'  Polished, realistic — gradient fills, wood tones,
 *                  glass sky-reflection, stone depth. Concept-render feel.
 *   - 'oneliner'   Single-weight ink strokes, no fills, no patterns.
 *                  Whitepaper / technical-drawing aesthetic.
 *
 * Scroll phases (700vh pinned):
 *   1 (0.00–0.12)  Hero stance · rotateX -22° · aerial-lite
 *   2 (0.12–0.40)  Engineered disassembly · layers lift with stagger
 *   3 (0.40–0.60)  Orbit from elevation → top-down plan view
 *   4 (0.60–0.82)  Guided interior flythrough (entrada → estar → piscina)
 *   5 (0.82–1.00)  Intelligent reassembly back to Phase 1
 *
 * Phase 1 rests at negative rotateX: the roof tilts toward the viewer
 * for an "approaching from above" feel (Wave 44.1 feedback).
 */

export type HouseVariant = 'materials' | 'oneliner';

export function HouseExploded({
  variant = 'materials',
  specs,
}: {
  variant?: HouseVariant;
  specs?: ProjectSpecs;
}) {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });

  // ── Disassembly + reassembly envelopes ─────────────────────────────
  // Single-story volume — no "upper floor walls" or "mid slab". Layers:
  //   roof (lifts highest), ceiling planks, glass wall, ripado,
  //   cobogó, stone, perimeter walls, foundation slab.
  const roofY    = useTransform(scrollYProgress, [0.14, 0.36, 0.86, 1.0], [0, -170, -170, 0]);
  const ceilingY = useTransform(scrollYProgress, [0.17, 0.38, 0.88, 1.0], [0, -95,  -95,  0]);
  const glassY   = useTransform(scrollYProgress, [0.20, 0.40, 0.90, 1.0], [0, -45,  -45,  0]);
  const ripadoX  = useTransform(scrollYProgress, [0.18, 0.38, 0.88, 1.0], [0,  55,   55,  0]);
  const cobogoX  = useTransform(scrollYProgress, [0.18, 0.38, 0.88, 1.0], [0, -55,  -55,  0]);
  const stoneX   = useTransform(scrollYProgress, [0.19, 0.39, 0.89, 1.0], [0,  75,   75,  0]);
  const wallsY   = useTransform(scrollYProgress, [0.22, 0.42, 0.92, 1.0], [0, -15,  -15,  0]);
  const slabY    = useTransform(scrollYProgress, [0.24, 0.44, 0.94, 1.0], [0,  30,   30,  0]);

  // ── Orbital camera + flythrough ──────────────────────────────────
  const rotateX = useTransform(
    scrollYProgress,
    [0, 0.12, 0.40, 0.55, 0.78, 0.88, 1.0],
    [-22, -22, -22, 70, 68, -22, -22],
  );
  const rotateZ = useTransform(
    scrollYProgress,
    [0, 0.35, 0.50, 0.70, 1.0],
    [0, 0, 2, -2, 0],
  );
  const scale = useTransform(
    scrollYProgress,
    [0, 0.12, 0.40, 0.55, 0.72, 0.82, 1.0],
    [1.0, 1.02, 1.0, 0.78, 1.5, 1.02, 1.0],
  );
  const translateY = useTransform(
    scrollYProgress,
    [0.55, 0.68, 0.82],
    ['0%', '-12%', '0%'],
  );

  const planOpacity = useTransform(
    scrollYProgress,
    [0.50, 0.60, 0.80, 0.85],
    [0, 1, 1, 0],
  );
  const shellOpacity = useTransform(
    scrollYProgress,
    [0.50, 0.60, 0.80, 0.85],
    [1, 0.18, 0.18, 1],
  );

  const ambientOpacity = useTransform(
    scrollYProgress,
    [0, 0.25, 0.5, 0.75, 1.0],
    [0.85, 1.0, 0.65, 1.0, 0.85],
  );

  const lblHero    = useRangeActive(scrollYProgress, 0.02, 0.10);
  const lblExplode = useRangeActive(scrollYProgress, 0.16, 0.36);
  const lblOrbit   = useRangeActive(scrollYProgress, 0.43, 0.54);
  const lblEntry   = useRangeActive(scrollYProgress, 0.60, 0.68);
  const lblLiving  = useRangeActive(scrollYProgress, 0.66, 0.72);
  const lblKitchen = useRangeActive(scrollYProgress, 0.70, 0.76);
  const lblPool    = useRangeActive(scrollYProgress, 0.74, 0.80);
  const lblClose   = useRangeActive(scrollYProgress, 0.88, 0.98);

  const motions: Motions = {
    roofY, ceilingY, glassY, ripadoX, cobogoX, stoneX, wallsY, slabY,
    shellOpacity, planOpacity,
  };

  return (
    <section
      ref={ref}
      className="relative h-[700vh]"
      aria-label="Gênese — apresentação da arquitetura"
    >
      <div
        className="sticky top-0 flex h-screen items-center justify-center overflow-hidden"
        style={{ perspective: '1800px', perspectiveOrigin: '50% 50%' }}
      >
        <motion.div
          aria-hidden
          className="absolute inset-0"
          style={{ opacity: ambientOpacity }}
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                variant === 'materials'
                  ? 'radial-gradient(ellipse 65% 50% at 50% 55%, rgba(212,184,150,0.10), transparent 70%)'
                  : 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(245,245,244,0.04), transparent 75%)',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 90% 25% at 50% 100%, rgba(0,0,0,0.50), transparent 70%)',
            }}
          />
        </motion.div>

        {/* The 3D materials variant has its OWN orbital camera driven by
            Three.js — we don't need the CSS perspective wrapper. The SVG
            oneliner keeps the CSS wrapper as before. */}
        {variant === 'materials' ? (
          <div className="absolute inset-0 z-10">
            <Suspense fallback={null}>
              <HouseScene3D progress={scrollYProgress} specs={specs} />
            </Suspense>
          </div>
        ) : (
          <motion.div
            className="relative z-10 flex items-center justify-center"
            style={{
              rotateX,
              rotateZ,
              scale,
              y: translateY,
              transformStyle: 'preserve-3d',
              willChange: 'transform',
            }}
          >
            <OnelinerSvg motions={motions} />
          </motion.div>
        )}

        {/* Narrative label overlay — z-20 so it sits above the z-10
            3D canvas. Without this the 3D materials canvas rendered
            on top of the text (2026-04-24 screenshot). */}
        <div className="pointer-events-none absolute inset-0 z-20">
          <Label y="top-[8%]" align="center" active={lblHero}>
            Um volume linear. Um teto que flutua.
          </Label>
          <Label y="top-[10%]" align="right" active={lblExplode}>
            Desmontagem de engenharia · camada por camada
          </Label>
          <Label y="top-[12%]" align="left" active={lblOrbit}>
            Da elevação à planta. Da admiração ao entendimento.
          </Label>
          <Label y="top-[16%]" align="center" active={lblEntry}>
            A entrada — um corte discreto, entre cobogó e pedra.
          </Label>
          <Label y="top-[16%]" align="center" active={lblLiving}>
            Sala, jantar, cozinha — mesma laje, mesmo deck.
          </Label>
          <Label y="top-[16%]" align="center" active={lblKitchen}>
            Quatro suítes. Cada uma, seu próprio banho.
          </Label>
          <Label y="top-[16%]" align="center" active={lblPool}>
            Piscina e prainha — três passos do fogão.
          </Label>
          <Label y="top-[10%]" align="center" active={lblClose}>
            Agora a casa não é só vista. É compreendida.
          </Label>
        </div>

        <div className="pointer-events-none absolute bottom-6 right-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
          <motion.span
            aria-hidden
            animate={{ opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            className="inline-block h-[1px] w-8 bg-accent"
          />
          role para continuar
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────── shared motion bundle passed to SVG variants

interface Motions {
  roofY: MotionValue<number>;
  ceilingY: MotionValue<number>;
  glassY: MotionValue<number>;
  ripadoX: MotionValue<number>;
  cobogoX: MotionValue<number>;
  stoneX: MotionValue<number>;
  wallsY: MotionValue<number>;
  slabY: MotionValue<number>;
  shellOpacity: MotionValue<number>;
  planOpacity: MotionValue<number>;
}


// ──────────────────────────────────── VARIANT B · one-liner
//
// Pure ink. No fills, no gradients, no patterns. Single stroke weight
// (1.1px) across every element — only opacity varies by hierarchy. The
// house reads as a technical drawing from a premium whitepaper. No
// texture degradation, no material complexity, just the line.

function OnelinerSvg({ motions }: { motions: Motions }) {
  const stroke = '#f5f5f4';
  const sw = 1.1; // default stroke width — uniform
  const sHair = 0.6; // hairlines for secondary detail
  return (
    <motion.svg
      viewBox="0 0 1200 520"
      className="w-[96vw] max-w-[1400px]"
      aria-hidden
      fill="none"
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <defs>
        {/* Only a soft pedestal glow — not a shadow, just grounding */}
        <radialGradient id="lShadow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#000" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#000" stopOpacity="0" />
        </radialGradient>
      </defs>

      <ellipse cx="600" cy="492" rx="420" ry="10" fill="url(#lShadow)" />

      <motion.g style={{ opacity: motions.shellOpacity }}>
        {/* Ground */}
        <line x1="40" y1="480" x2="1160" y2="480" stroke={stroke} strokeOpacity="0.35" strokeWidth={sw} />
        <text x="40" y="500" fontSize="9" letterSpacing="3" fill={stroke} fillOpacity="0.35" stroke="none">
          TERRENO · NÍVEL ±0,00 · 29,28 m LINEARES
        </text>

        {/* Slab */}
        <motion.g style={{ y: motions.slabY }}>
          <rect x="80" y="455" width="1040" height="22" stroke={stroke} strokeOpacity="0.8" strokeWidth={sw} />
          <text x="88" y="470" fontSize="8" letterSpacing="2" fill={stroke} fillOpacity="0.4" stroke="none">RADIER + LAJE</text>
        </motion.g>

        {/* Perimeter walls */}
        <motion.g style={{ y: motions.wallsY }}>
          <rect x="100" y="230" width="1000" height="225" stroke={stroke} strokeOpacity="0.9" strokeWidth={sw} />
          {/* Internal dividers — hairlines */}
          {/* Wave 73C · solid hairlines (no dasharray). JP didn't see the
              point of the dots — a one-liner is one weight, one stroke. */}
          <line x1="200" y1="230" x2="200" y2="455" stroke={stroke} strokeOpacity="0.28" strokeWidth={sHair} />
          <line x1="640" y1="230" x2="640" y2="455" stroke={stroke} strokeOpacity="0.28" strokeWidth={sHair} />
          <line x1="950" y1="230" x2="950" y2="455" stroke={stroke} strokeOpacity="0.28" strokeWidth={sHair} />
        </motion.g>

        {/* Cobogó — suggested as a grid of small circles, same 1.1 stroke */}
        <motion.g style={{ x: motions.cobogoX }}>
          <rect x="110" y="244" width="90" height="210" stroke={stroke} strokeOpacity="0.75" strokeWidth={sw} />
          {Array.from({ length: 5 }).flatMap((_, row) =>
            Array.from({ length: 3 }).map((_, col) => (
              <circle
                key={`${row}-${col}`}
                cx={125 + col * 28}
                cy={260 + row * 38}
                r="5.5"
                stroke={stroke}
                strokeOpacity="0.55"
                strokeWidth={sHair}
              />
            ))
          )}
          <text x="155" y="236" fontSize="8" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.7" stroke="none">COBOGÓ</text>
        </motion.g>

        {/* Ripado — vertical hairlines spaced evenly. No fill. */}
        <motion.g style={{ x: motions.ripadoX }}>
          <rect x="205" y="250" width="430" height="200" stroke={stroke} strokeOpacity="0.5" strokeWidth={sw} />
          {Array.from({ length: 24 }).map((_, i) => (
            <line
              key={i}
              x1={213 + i * 17.5}
              y1="254"
              x2={213 + i * 17.5}
              y2="446"
              stroke={stroke}
              strokeOpacity="0.4"
              strokeWidth={sHair}
            />
          ))}
          {/* Porta pivotante — clean rectangle + pivot hinge line */}
          <rect x="388" y="340" width="52" height="110" stroke={stroke} strokeOpacity="0.9" strokeWidth={sw} />
          <line x1="438" y1="340" x2="438" y2="450" stroke={stroke} strokeOpacity="0.6" strokeWidth={sHair} />
          <text x="414" y="338" fontSize="7" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.7" stroke="none">PORTA PIVOTANTE</text>
        </motion.g>

        {/* Brise telado — central façade band per the real Fachada
            Frontal. 5 tall panels with 4 narrow glass slits between
            them. In one-liner aesthetic each panel is just an outlined
            rectangle with a few internal hairlines suggesting the
            woven screen; slits are thinner outlined rectangles. The
            glass wall (17m run) is on the COURTYARD face — drawn in
            the plan view below, not on this elevation. */}
        <motion.g style={{ y: motions.glassY }}>
          {[0, 1, 2, 3, 4].map((i) => {
            const panelW = 50;
            const gap = 8;
            const x0 = 640;
            const x = x0 + i * (panelW + gap);
            return (
              <g key={`brise-${i}`}>
                <rect x={x} y="255" width={panelW} height="200" stroke={stroke} strokeOpacity="0.75" strokeWidth={sw} />
                {/* Three hairlines suggesting the woven telado */}
                <line x1={x + panelW * 0.25} y1="260" x2={x + panelW * 0.25} y2="450" stroke={stroke} strokeOpacity="0.25" strokeWidth={sHair} />
                <line x1={x + panelW * 0.5}  y1="260" x2={x + panelW * 0.5}  y2="450" stroke={stroke} strokeOpacity="0.25" strokeWidth={sHair} />
                <line x1={x + panelW * 0.75} y1="260" x2={x + panelW * 0.75} y2="450" stroke={stroke} strokeOpacity="0.25" strokeWidth={sHair} />
              </g>
            );
          })}
          {[0, 1, 2, 3].map((i) => {
            const panelW = 50;
            const gap = 8;
            const x0 = 640;
            const x = x0 + panelW + i * (panelW + gap);
            return (
              <rect key={`slit-${i}`} x={x} y="270" width={gap} height="170" stroke={stroke} strokeOpacity="0.4" strokeWidth={sHair} />
            );
          })}
          <text x="785" y="252" fontSize="8" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.7" stroke="none">BRISE TELADO · ALUMÍNIO OU MADEIRA ECOLÓGICA</text>
        </motion.g>

        {/* Stone — represented as an irregular line field, no fill */}
        <motion.g style={{ x: motions.stoneX }}>
          <rect x="955" y="244" width="150" height="210" stroke={stroke} strokeOpacity="0.8" strokeWidth={sw} />
          {/* A few horizontal irregular lines to suggest stone courses */}
          <line x1="965" y1="270" x2="1095" y2="272" stroke={stroke} strokeOpacity="0.35" strokeWidth={sHair} />
          <line x1="965" y1="298" x2="1095" y2="296" stroke={stroke} strokeOpacity="0.35" strokeWidth={sHair} />
          <line x1="965" y1="326" x2="1095" y2="328" stroke={stroke} strokeOpacity="0.35" strokeWidth={sHair} />
          {/* Garage opening as clean rect */}
          <rect x="980" y="350" width="100" height="105" stroke={stroke} strokeOpacity="0.75" strokeWidth={sw} />
          <text x="1030" y="236" fontSize="8" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.7" stroke="none">PEDRAS MOÍDO</text>
        </motion.g>

        {/* Ceiling band */}
        <motion.g style={{ y: motions.ceilingY }}>
          <rect x="94" y="222" width="1012" height="8" stroke={stroke} strokeOpacity="0.6" strokeWidth={sw} />
        </motion.g>

        {/* Roof */}
        <motion.g style={{ y: motions.roofY }}>
          <rect x="60" y="198" width="1080" height="24" stroke={stroke} strokeOpacity="1" strokeWidth={sw} />
          {/* Seams as hairlines */}
          <line x1="60" y1="206" x2="1140" y2="206" stroke={stroke} strokeOpacity="0.25" strokeWidth={sHair} />
          <line x1="60" y1="214" x2="1140" y2="214" stroke={stroke} strokeOpacity="0.2" strokeWidth={sHair} />
          {/* Duto */}
          <rect x="62" y="172" width="38" height="28" stroke={stroke} strokeOpacity="0.7" strokeWidth={sw} />
          <text x="81" y="192" fontSize="7" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.55" stroke="none">DUTO</text>
        </motion.g>
      </motion.g>

      {/* Plan overlay */}
      <motion.g style={{ opacity: motions.planOpacity }}>
        <rect x="60" y="60" width="1080" height="400" stroke={stroke} strokeOpacity="0.22" strokeWidth={sHair} />
        <text x="60" y="54" fontSize="8" letterSpacing="2" fill={stroke} fillOpacity="0.4" stroke="none">LOTE 10 · LOTE 09</text>

        <g transform="translate(100 95)">
          <rect width="180" height="105" stroke={stroke} strokeOpacity="0.8" strokeWidth={sw} />
          <line x1="120" y1="0" x2="120" y2="105" stroke={stroke} strokeOpacity="0.5" strokeWidth={sHair} />
          <text x="60" y="58" fontSize="9" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.9" stroke="none">PISCINA</text>
          <text x="150" y="58" fontSize="7" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.7" stroke="none">PRAINHA</text>
        </g>
        <g transform="translate(100 210)">
          <rect width="260" height="90" stroke={stroke} strokeOpacity="0.4" strokeWidth={sHair} />
          <text x="60" y="50" fontSize="9" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.85" stroke="none">SOLARIUM</text>
          <text x="200" y="50" fontSize="9" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.85" stroke="none">GOURMET</text>
        </g>
        <g transform="translate(370 95)">
          <rect width="60" height="210" stroke={stroke} strokeOpacity="0.22" strokeWidth={sHair} />
          <text x="30" y="110" fontSize="8" textAnchor="middle" letterSpacing="3" fill={stroke} fillOpacity="0.65" stroke="none" transform="rotate(-90 30 110)">JARDIM</text>
        </g>
        <g transform="translate(100 310)">
          <rect width="330" height="90" stroke={stroke} strokeOpacity="0.85" strokeWidth={sw} />
          <line x1="110" y1="0" x2="110" y2="90" stroke={stroke} strokeOpacity="0.45" strokeWidth={sHair} />
          <line x1="220" y1="0" x2="220" y2="90" stroke={stroke} strokeOpacity="0.45" strokeWidth={sHair} />
          <text x="55" y="50" fontSize="9" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.95" stroke="none">COZINHA</text>
          <text x="165" y="50" fontSize="9" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.95" stroke="none">JANTAR</text>
          <text x="275" y="50" fontSize="9" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.95" stroke="none">ESTAR</text>
        </g>
        <g transform="translate(430 310)">
          <rect width="60" height="90" stroke={stroke} strokeOpacity="0.7" strokeWidth={sw} />
          <text x="30" y="40" fontSize="8" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.9" stroke="none">HALL</text>
          <text x="30" y="54" fontSize="8" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.9" stroke="none">ENTRADA</text>
        </g>
        <g transform="translate(495 95)">
          <rect width="30" height="305" stroke={stroke} strokeOpacity="0.32" strokeWidth={sHair} />
        </g>
        {/* 4 suites stacked N→S in the plan view. S1-S3 are the 12.30 m²
            standard suites (3.6 × 3.4), Suite 4 is the 16.34 m² master
            (3.6 × 4.5) — visibly taller in the diagram. Heights chosen
            proportionally: standard = 60 px, master = 80 px. Runs from
            S4 at the top to S1 at the bottom (closest to the spine). */}
        {[
          { label: 4, y: 95,  h: 80 },  // master
          { label: 3, y: 180, h: 60 },
          { label: 2, y: 245, h: 60 },
          { label: 1, y: 310, h: 60 },
        ].map((s) => (
          <g key={s.label} transform={`translate(530 ${s.y})`}>
            <rect width="120" height={s.h} stroke={stroke} strokeOpacity="0.7" strokeWidth={sw} />
            <text x="60" y={s.h / 2 + 3} fontSize="9" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.9" stroke="none">
              SUÍTE {s.label}{s.label === 4 ? ' · MASTER' : ''}
            </text>
            <rect x="120" width="40" height={s.h} stroke={stroke} strokeOpacity="0.45" strokeWidth={sHair} />
            <text x="140" y={s.h / 2 + 3} fontSize="7" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.6" stroke="none">
              BANHO
            </text>
          </g>
        ))}
        <g transform="translate(700 310)">
          <rect width="200" height="90" stroke={stroke} strokeOpacity="0.7" strokeWidth={sw} />
          <text x="100" y="30" fontSize="9" textAnchor="middle" letterSpacing="2" fill={stroke} fillOpacity="0.9" stroke="none">GARAGEM · 3 CARROS</text>
          {[0, 1, 2].map((i) => (
            <rect key={i} x={20 + i * 55} y="45" width="40" height="35" stroke={stroke} strokeOpacity="0.35" strokeWidth={sHair} />
          ))}
        </g>
        {/* Glass run as a single bold hairline */}
        <line x1="100" y1="400" x2="430" y2="400" stroke={stroke} strokeOpacity="0.8" strokeWidth="1.4" />
        <text x="265" y="414" fontSize="8" textAnchor="middle" letterSpacing="3" fill={stroke} fillOpacity="0.85" stroke="none">VIDRO CONTÍNUO · 17 m</text>
        <path
          d="M 460 355 C 420 355, 380 355, 340 355 S 200 355, 160 310 S 130 200, 175 150"
          stroke={stroke} strokeOpacity="0.8" strokeWidth={sw}
          fill="none"
        />
        <circle cx="175" cy="150" r="4" fill={stroke} stroke="none" />
        <circle cx="460" cy="355" r="3" fill={stroke} fillOpacity="0.55" stroke="none" />
        <text x="600" y="446" fontSize="8" textAnchor="middle" letterSpacing="4" fill={stroke} fillOpacity="0.4" stroke="none">RUA 25</text>
      </motion.g>
    </motion.svg>
  );
}

// ─────────────────────────────────────────────────────── helpers

function Label({
  y,
  align,
  active,
  children,
}: {
  y: string;
  align: 'left' | 'right' | 'center';
  active: MotionValue<number>;
  children: React.ReactNode;
}) {
  const justify =
    align === 'center' ? 'justify-center text-center' :
    align === 'right'  ? 'justify-end text-right' :
                         'justify-start text-left';
  const padding =
    align === 'center' ? 'px-8' :
    align === 'right'  ? 'pr-10 sm:pr-16 lg:pr-24' :
                         'pl-10 sm:pl-16 lg:pl-24';
  const cls =
    align === 'center'
      ? 'font-display text-[clamp(1.25rem,3vw,2rem)] font-light leading-tight tracking-tight text-ink [text-wrap:balance] max-w-[30ch]'
      : 'font-display text-[13px] font-light uppercase tracking-[0.25em] text-accent sm:text-sm';
  return (
    <motion.div
      className={`absolute inset-x-0 ${y} flex ${justify} ${padding}`}
      style={{ opacity: active }}
    >
      <span className={cls}>{children}</span>
    </motion.div>
  );
}

function useRangeActive(
  progress: MotionValue<number>,
  start: number,
  end: number,
): MotionValue<number> {
  const mid1 = start + (end - start) * 0.3;
  const mid2 = start + (end - start) * 0.7;
  return useTransform(progress, [start, mid1, mid2, end], [0, 1, 1, 0]);
}
